import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useDataProvider, Loading } from "react-admin";
import ReactECharts from "echarts-for-react";

interface ResolutionGaugeData {
  rate: number; // percentage 0-100
  goal: number; // target percentage, e.g. 95
  createdCount: number;
  resolvedWithinWindow: number;
}

const RmfResolutionGauge: React.FC = () => {
  const theme = useTheme();
  const dataProvider = useDataProvider();
  const [chartData, setChartData] = useState<ResolutionGaugeData | null>(null);

  useEffect(() => {
    let isMounted = true;
    setChartData(null);

    const fetchData = async () => {
      try {
        const response = await dataProvider.getChart(
          "rmf/analytics/resolution-efficiency-gauge",
          {}
        );
        if (!isMounted) return;
        setChartData(response as ResolutionGaugeData);
      } catch (error) {
        if (isMounted) {
          setChartData(null);
          console.error(
            "Error fetching RMF resolution efficiency data:",
            error
          );
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [dataProvider]);

  const option = useMemo(() => {
    if (!chartData) return undefined;

    const value = Math.max(0, Math.min(100, chartData.rate));
    const goal = chartData.goal || 70;

    // Color zones: red [0, 30), yellow [30, goal), green [goal, 100]
    const redEnd = 0.3;
    const goalFraction = Math.min(1, Math.max(0, goal / 100));

    return {
      title: {
        text: "30-Day Close Rate",
        left: "center",
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          fontWeight: "bold",
          fontSize: "1.5em",
        },
      },
      tooltip: {
        formatter: () =>
          `Resolution Rate: ${value.toFixed(1)}%<br/>Goal: ${goal.toFixed(
            1
          )}%<br/>Created/Reopened (30d): ${
            chartData.createdCount
          }<br/>Resolved (30d): ${chartData.resolvedWithinWindow}`,
      },
      series: [
        {
          type: "gauge",
          min: 0,
          max: 100,
          center: ["50%", "50%"],
          axisLine: {
            lineStyle: {
              width: 18,
              color: [
                [redEnd, "#d32f2f"], // red
                [goalFraction, "#f9a825"], // yellow
                [1, "#388e3c"], // green
              ],
            },
          },
          axisLabel: {
            color: "inherit",
            distance: 25,
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            distance: -30,
            length: 30,
            lineStyle: {
              color: theme.palette.mode === "dark" ? "#222" : "#fff",
              width: 4,
            },
          },
          pointer: {
            icon: "path://M2090.36389,615.30999 L2090.36389,615.30999 C2091.48372,615.30999 2092.40383,616.194028 2092.44859,617.312956 L2096.90698,728.755929 C2097.05155,732.369577 2094.2393,735.416212 2090.62566,735.56078 C2090.53845,735.564269 2090.45117,735.566014 2090.36389,735.566014 L2090.36389,735.566014 C2086.74736,735.566014 2083.81557,732.63423 2083.81557,729.017692 C2083.81557,728.930412 2083.81732,728.84314 2083.82081,728.755929 L2088.2792,617.312956 C2088.32396,616.194028 2089.24407,615.30999 2090.36389,615.30999 Z",
            length: "85%",
            width: 8,
            itemStyle: {
              color: theme.palette.primary.dark,
            },
          },
          detail: {
            formatter: "{value}%",
            fontSize: 20,
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
            offsetCenter: [0, "80%"],
          },
          data: [
            {
              value,
            },
          ],
        },
      ],
    };
  }, [chartData, theme.palette.mode, theme.palette.primary.dark]);

  return (
    <Card
      sx={{
        height: 300,
        width: "100%",
        padding: "1em",
      }}
    >
      <CardContent>
        {!chartData ? (
          <Loading />
        ) : chartData.createdCount === 0 ? (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              No Tickets Created in the Last 30 Days
            </Typography>
          </Box>
        ) : option ? (
          <ReactECharts option={option} />
        ) : null}
      </CardContent>
    </Card>
  );
};

export default RmfResolutionGauge;
